import { useState, useCallback, useEffect } from 'react'
import { funnelApi } from '../api'
import type { FunnelRule, RuleTrigger, RuleAction } from '../types'

/**
 * Rule CRUD operations and trigger/action management.
 * Now backed by the real Go API via funnelApi.
 */
function useRuleMutations(
  funnelId: string | undefined,
  setRules: React.Dispatch<React.SetStateAction<FunnelRule[]>>,
  rulesLength: number
) {
  const addRule = useCallback(async () => {
    const newRule: FunnelRule = {
      id: `rule-${Date.now()}`,
      name: `Rule ${rulesLength + 1}`,
      logic: 'AND',
      triggers: [{
        id: `trigger-${Date.now()}`,
        type: 'cart_value',
        operator: 'greater_than',
        value: '0',
      }],
      actions: [],
      isActive: true,
      priority: rulesLength,
    }

    if (funnelId) {
      try {
        const response = await funnelApi.createRule(funnelId, newRule)
        setRules((prev) => [...prev, response.data])
        return response.data
      } catch (error) {
        console.error('[useFunnelRules] Failed to create rule:', error)
      }
    }

    // Fallback to local state if no funnelId
    setRules((prev) => [...prev, newRule])
    return newRule
  }, [funnelId, rulesLength, setRules])

  const updateRule = useCallback(async (ruleId: string, updates: Partial<FunnelRule>) => {
    setRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, ...updates } : r))
    )

    if (funnelId) {
      try {
        // Get the full updated rule from state
        setRules((prev) => {
          const rule = prev.find((r) => r.id === ruleId)
          if (rule) {
            funnelApi.updateRule(funnelId, ruleId, rule).catch((error) => {
              console.error('[useFunnelRules] Failed to update rule:', error)
            })
          }
          return prev
        })
      } catch (error) {
        console.error('[useFunnelRules] Failed to update rule:', error)
      }
    }
  }, [funnelId, setRules])

  const deleteRule = useCallback(async (ruleId: string) => {
    setRules((prev) => prev.filter((r) => r.id !== ruleId))

    if (funnelId) {
      try {
        await funnelApi.deleteRule(funnelId, ruleId)
      } catch (error) {
        console.error('[useFunnelRules] Failed to delete rule:', error)
      }
    }
  }, [funnelId, setRules])

  const addTrigger = useCallback((ruleId: string) => {
    const trigger: RuleTrigger = {
      id: `trigger-${Date.now()}`,
      type: 'cart_value',
      operator: 'greater_than',
      value: '',
    }
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId ? { ...r, triggers: [...r.triggers, trigger] } : r
      )
    )
    return trigger
  }, [setRules])

  const updateTrigger = useCallback((ruleId: string, triggerId: string, updates: Partial<RuleTrigger>) => {
    setRules((prev) => {
      const updated = prev.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              triggers: r.triggers.map((t) =>
                t.id === triggerId ? { ...t, ...updates } : t
              ),
            }
          : r
      )

      // Persist to API
      if (funnelId) {
        const rule = updated.find((r) => r.id === ruleId)
        if (rule) {
          funnelApi.updateRule(funnelId, ruleId, rule).catch((error) => {
            console.error('[useFunnelRules] Failed to persist trigger update:', error)
          })
        }
      }

      return updated
    })
  }, [funnelId, setRules])

  const deleteTrigger = useCallback((ruleId: string, triggerId: string) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? { ...r, triggers: r.triggers.filter((t) => t.id !== triggerId) }
          : r
      )
    )
  }, [setRules])

  const addAction = useCallback((ruleId: string) => {
    const action: RuleAction = {
      id: `action-${Date.now()}`,
      type: 'show_step',
      target: '',
    }
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId ? { ...r, actions: [...r.actions, action] } : r
      )
    )
    return action
  }, [setRules])

  const updateAction = useCallback((ruleId: string, actionId: string, updates: Partial<RuleAction>) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              actions: r.actions.map((a) =>
                a.id === actionId ? { ...a, ...updates } : a
              ),
            }
          : r
      )
    )
  }, [setRules])

  const deleteAction = useCallback((ruleId: string, actionId: string) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? { ...r, actions: r.actions.filter((a) => a.id !== actionId) }
          : r
      )
    )
  }, [setRules])

  return {
    addRule,
    updateRule,
    deleteRule,
    addTrigger,
    updateTrigger,
    deleteTrigger,
    addAction,
    updateAction,
    deleteAction,
  }
}

/** Rules management hook backed by the Go API.
 *  When funnelId is provided, loads rules from the API and persists changes.
 *  Falls back to local state when funnelId is not available.
 */
export function useFunnelRules(funnelId?: string, initialRules: FunnelRule[] = []) {
  const [rules, setRules] = useState<FunnelRule[]>(initialRules)
  const [loading, setLoading] = useState(false)

  // Load rules from API when funnelId is available
  useEffect(() => {
    if (!funnelId) return

    let cancelled = false
    setLoading(true)

    funnelApi.getRules(funnelId)
      .then((response) => {
        if (!cancelled) {
          setRules(response.data)
        }
      })
      .catch((error) => {
        console.error('[useFunnelRules] Failed to load rules:', error)
        if (!cancelled) {
          setRules(initialRules)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [funnelId]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    addRule,
    updateRule,
    deleteRule,
    addTrigger,
    updateTrigger,
    deleteTrigger,
    addAction,
    updateAction,
    deleteAction,
  } = useRuleMutations(funnelId, setRules, rules.length)

  return {
    rules,
    loading,
    setRules,
    addRule,
    updateRule,
    deleteRule,
    addTrigger,
    updateTrigger,
    deleteTrigger,
    addAction,
    updateAction,
    deleteAction,
  }
}
